import { useEffect, useRef, useState } from 'react';
import { Search, CalendarDays, X } from 'lucide-react';
import { useLanguage } from '../../../features/language/useLanguage';
import { openNativeDatePickerSafely, preventManualDateEntry, preventManualDatePasteOrDrop } from '../../../utils/date';

interface SalesOrderFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  status: string;
  onStatusChange: (value: string) => void;
  startDate: string;
  endDate: string;
  onApplyDateRange: (start: string, end: string) => void;
}

/** Search + status + order-date-range filters for the sales order list. */
export function SalesOrderFilters({
  search,
  onSearchChange,
  status,
  onStatusChange,
  startDate,
  endDate,
  onApplyDateRange,
}: SalesOrderFiltersProps) {
  const { t } = useLanguage();
  const [isDatePanelOpen, setIsDatePanelOpen] = useState(false);
  const [draftStart, setDraftStart] = useState('');
  const [draftEnd, setDraftEnd] = useState('');
  const datePopoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePopoverRef.current && !datePopoverRef.current.contains(event.target as Node)) {
        setIsDatePanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDatePanel = () => {
    setIsDatePanelOpen((prev) => {
      const next = !prev;
      // Seed the draft inputs from the applied filter each time the panel opens.
      if (next) {
        setDraftStart(startDate);
        setDraftEnd(endDate);
      }
      return next;
    });
  };

  const applyDateFilter = () => {
    onApplyDateRange(draftStart, draftEnd);
    setIsDatePanelOpen(false);
  };

  return (
    <div className="mb-6 relative w-full">
      <div className="flex flex-wrap items-end gap-3 bg-white p-1 rounded-lg">
        <div className="flex-grow min-w-[280px]">
          <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1 block">
            {t('salesOrder.list.filters.search')}
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t('salesOrder.list.filters.searchPlaceholder')}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
              data-test-id="so-search-filter"
            />
          </div>
        </div>

        <div className="w-full md:w-[180px]">
          <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1 block">
            {t('salesOrder.list.filterStatus')}
          </label>
          <select
            data-test-id="so-status-filter"
            value={status}
            onChange={(e) => onStatusChange(e.target.value)}
            className="w-full px-2 py-2 text-sm border border-gray-200 rounded-md bg-gray-50 hover:bg-white cursor-pointer transition-colors outline-none focus:border-blue-500"
          >
            <option value="all">{t('salesOrder.list.status.all')}</option>
            <option value="Draft">{t('salesOrder.list.status.draft')}</option>
            <option value="Sent">{t('salesOrder.list.status.sent')}</option>
            <option value="Delivered">{t('salesOrder.list.status.delivered')}</option>
            <option value="Cancelled">{t('salesOrder.list.status.cancelled')}</option>
          </select>
        </div>

        <div className="relative" ref={datePopoverRef}>
          <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1 block">
            {t('common.dateRange.label')}
          </label>
          <button
            type="button"
            onClick={toggleDatePanel}
            className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-all ${
              isDatePanelOpen
                ? 'bg-blue-50 border-primary-500 text-primary-600'
                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'
            }`}
            data-test-id="so-date-filter-toggle"
          >
            <CalendarDays className="h-4 w-4" />
            {t('salesOrder.list.filters.dates')}
          </button>

          {isDatePanelOpen && (
            <div className="absolute left-0 md:left-auto md:right-0 z-50 mt-2 w-[320px] sm:w-[380px] max-w-[calc(100vw-1rem)] bg-white rounded-xl shadow-2xl border border-gray-200 p-4 animate-in fade-in zoom-in duration-150">
              <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-50">
                <h3 className="text-sm font-bold text-gray-800">{t('salesOrder.list.filters.dateRangeFilters')}</h3>
                <button onClick={() => setIsDatePanelOpen(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="space-y-3 bg-gray-50 p-3 rounded-lg">
                <p className="text-xs font-bold text-gray-700 mb-1">{t('salesOrder.list.filters.orderDate')}</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">{t('common.dateRange.startDate')}</label>
                    <input
                      type="date"
                      value={draftStart}
                      onChange={(e) => setDraftStart(e.target.value)}
                      onClick={openNativeDatePickerSafely}
                      onKeyDown={preventManualDateEntry}
                      onPaste={preventManualDatePasteOrDrop}
                      onDrop={preventManualDatePasteOrDrop}
                      className="w-full p-2 text-xs border border-gray-200 rounded bg-white outline-none focus:border-blue-500"
                      data-test-id="so-date-filter-start"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">{t('common.dateRange.endDate')}</label>
                    <input
                      type="date"
                      value={draftEnd}
                      onChange={(e) => setDraftEnd(e.target.value)}
                      onClick={openNativeDatePickerSafely}
                      onKeyDown={preventManualDateEntry}
                      onPaste={preventManualDatePasteOrDrop}
                      onDrop={preventManualDatePasteOrDrop}
                      className="w-full p-2 text-xs border border-gray-200 rounded bg-white outline-none focus:border-blue-500"
                      data-test-id="so-date-filter-end"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsDatePanelOpen(false)}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                >
                  {t('auth.common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={applyDateFilter}
                  className="px-4 py-1.5 text-xs font-semibold bg-primary-600 text-white rounded-md hover:bg-primary-700 shadow-sm transition-colors"
                >
                  {t('salesOrder.list.filters.applyFilters')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
